/**
 * La question du navigateur en cours (« Autoriser le micro ? »), vue depuis la page.
 *
 * La page ne voit pas la fenêtre du navigateur : elle sait seulement qu'une demande d'appareil est
 * partie et n'a pas encore répondu. Chaque demande faite dans un geste de l'élève passe par
 * `watchPermissionPrompt` : le test d'entrée (`probeWeb`), et en séance « Activer mon micro », le
 * micro de la barre, « Réessayer » (`useMicRefusal`). Le guidage (`<PermissionPromptCoach>`) lit
 * `usePermissionPrompt` ; les boîtes du live attendent la réponse (`usePermissionRequestPending`).
 *
 * La règle : **on ne guide que vers une question qui est vraiment à l'écran.**
 *  - l'accès déjà acquis (`deviceAccessReady`, la règle de `useMicGate`) : rien, la demande se résout
 *    sans question ;
 *  - sinon, la question n'est dite à l'écran qu'au bout de `PROMPT_DELAY_MS` sans réponse : ouvrir un
 *    micro déjà autorisé, un refus d'office (Safari dans un document déjà refusé, Chrome qui a mis la
 *    question en sourdine) répondent avant, et rien ne clignote ;
 *  - la réponse de l'élève (accordé ou refusé) l'efface aussitôt ; « OK » la ferme, pour une question
 *    que le navigateur a repliée sans répondre (Firefox, un clic hors de sa fenêtre).
 *
 * La demande suivie est la réponse du navigateur, pas ce qui vient après : en séance, l'appareil acquis
 * ou refusé, pas la publication du micro dans la salle, qui peut attendre une reconnexion.
 *
 * Au web seulement : dans l'app, c'est le téléphone qui pose sa question, au milieu de l'écran.
 */
import { useEffect, useState, useSyncExternalStore } from "react"

import { bugsnagClient } from "../shared/bugsnagClient"
import { IS_WEB } from "../shared/platform"
import { deviceAccessReady } from "./deviceAccess"

/** Ce que la question demande. */
export type PermissionPromptKind = "microphone" | "camera" | "both"

/** Le temps au bout duquel une demande sans réponse est une question à l'écran. */
export const PROMPT_DELAY_MS = 400

type Shown = { id: number; kind: PermissionPromptKind }

let shown: Shown | null = null
const inFlight = new Set<number>()
let nextId = 0
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((listener) => listener())

function show(next: Shown | null) {
    if (next === shown) return
    shown = next
    notify()
}

/** Les permissions que la question porte, pour savoir si elle sera posée. */
const NAMES: Record<PermissionPromptKind, ("microphone" | "camera")[]> = {
    microphone: ["microphone"],
    camera: ["camera"],
    both: ["microphone", "camera"],
}

async function alreadyGranted(kind: PermissionPromptKind): Promise<boolean> {
    const ready = await Promise.all(NAMES[kind].map(deviceAccessReady))
    return ready.every(Boolean)
}

/**
 * Suit une demande d'appareil faite dans le geste de l'élève, et la rend telle quelle. Appelée APRÈS
 * l'avoir lancée : la demande part dans le geste, rien ne s'attend avant elle.
 *
 * `completion` : ce qui suit une réponse accordée — en séance, la publication du micro dans la salle. La
 * carte part à la réponse, mais la demande reste en cours jusque-là : « Active ton micro » ne se remontre
 * pas le temps que le micro s'ouvre. Un refus la termine aussitôt (la boîte du refus peut paraître).
 */
export function watchPermissionPrompt<T>(
    kind: PermissionPromptKind,
    request: Promise<T>,
    completion?: Promise<unknown>
): Promise<T> {
    if (!IS_WEB) return request
    const id = ++nextId
    let answered = false
    inFlight.add(id)
    notify()
    const answer = (outcome: "answered" | "refused") => {
        answered = true
        if (shown?.id === id) {
            bugsnagClient.leaveBreadcrumb("permission prompt: closed", { kind, outcome })
            shown = null
        }
    }
    const finish = () => {
        inFlight.delete(id)
        notify()
    }
    request.then(
        () => {
            answer("answered")
            if (completion == null) finish()
            else notify()
        },
        () => {
            answer("refused")
            finish()
        }
    )
    completion?.then(finish, finish)
    void alreadyGranted(kind).then((granted) => {
        if (granted || answered) return
        setTimeout(() => {
            if (answered) return
            bugsnagClient.leaveBreadcrumb("permission prompt: shown", { kind })
            show({ id, kind })
        }, PROMPT_DELAY_MS)
    })
    return request
}

/** « OK » : le guidage se ferme, la question du navigateur (s'il la montre encore) reste la sienne. */
export function dismissPermissionPrompt() {
    show(null)
}

function subscribe(listener: () => void) {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

const shownKind = () => shown?.kind ?? null
const pending = () => inFlight.size > 0

/** La question en cours à l'écran, ou `null`. */
export function usePermissionPrompt(): PermissionPromptKind | null {
    return useSyncExternalStore(subscribe, shownKind, shownKind)
}

/**
 * Une demande attend la réponse du navigateur, question à l'écran ou non : « Active ton micro » ne se
 * remontre pas pendant ce temps, même la carte fermée par « OK ».
 *
 * Une mise à jour ordinaire, pas synchrone (`useSyncExternalStore` rend aussitôt) : la fin de la demande se
 * rend avec l'ouverture de la porte du micro (`useMicGate.opened`), posée dans la même suite de la demande.
 * Rendue seule, avant elle, elle laissait « Active ton micro » revenir un instant (relevé à l'image près,
 * recette du 26/10).
 */
export function usePermissionRequestPending(): boolean {
    const [value, setValue] = useState(pending)
    useEffect(() => {
        const update = () => setValue(pending())
        update()
        return subscribe(update)
    }, [])
    return value
}

/** Pour les tests : aucune demande en cours. */
export function resetPermissionPromptForTests() {
    shown = null
    inFlight.clear()
}
